import {
  ActionButton,
  BadgeV2,
  Icon,
  Modal,
  SearchBox,
  Spinner,
  Text,
} from "@jds/core";
import "../Styles/requests.css";
import "../Styles/FilterPanel.css";

import {
  IcInfo,
  IcSearch,
  IcSort,
  IcStar,
  IcSuccessColored,
  IcTime,
  IcVisible,
} from "@jds/core-icons";
import { useEffect, useState, useMemo } from "react";
import "../Styles/configurePage.css";
import {
  getGrievnaceRequestByGrievanceTemplateId,
  getGrievnaceRequestByUserId,
  translateData,
  translateConfiguration,
} from "../store/actions/CommonAction";
import { useDispatch, useSelector } from "react-redux";
import React from "react";
import { useNavigate } from "react-router";
import { GRIEVANCE_STATUS } from "../store/constants/Constants";
import StarRating from "./StarRating";
import SessionExpiredModal from "./SessionExpiredModal";
import useTranslation, { getApiValueFromLanguage } from "../hooks/useTranslation";

const formatGrievanceDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getGrievanceStatusBadgeClass = (status) => {
  if (
    status === GRIEVANCE_STATUS.GRIEVANCE_L1_ESCALATED ||
    status === GRIEVANCE_STATUS.GRIEVANCE_L2_ESCALATED
  ) {
    return "grievance-status-badge grievance-status-badge--escalated";
  }
  if (status === GRIEVANCE_STATUS.GRIEVANCE_RESOLVED) {
    return "grievance-status-badge grievance-status-badge--resolved";
  }
  if (status === GRIEVANCE_STATUS.GRIEVANCE_INPROCESS) {
    return "grievance-status-badge grievance-status-badge--inprocess";
  }
  return "grievance-status-badge grievance-status-badge--default";
};

const Requests = () => {
  const [loadLogs, setLoadLogs] = useState(true);
  const tenantId = useSelector((state) => state.common.tenant_id);
  const [expandedRow, setExpandedRow] = useState(null);
  const dispatch = useDispatch();
  const [grievanceData, setGrievanceData] = useState([]);
  const [resolvedCount, setResolvedCount] = useState(0);
  const [inProgressCount, setInProgressCount] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const [sessionExpired, setSessionExpired] = useState(false);
  const grievanceId = useSelector(
    (state) => state.common.grievance_template_id
  );

  // Calculate counts when grievanceData changes
  useEffect(() => {
    if (grievanceData && grievanceData.length > 0) {
      let newCnt = 0;
      let inProcessCnt = 0;
      let resolvedCnt = 0;

      grievanceData.forEach((grievance) => {
        const status = grievance?.status;
        if (status === GRIEVANCE_STATUS.NEW) {
          newCnt++;
        } else if (status === GRIEVANCE_STATUS.GRIEVANCE_RESOLVED) {
          resolvedCnt++;
        } else if (
          status === GRIEVANCE_STATUS.GRIEVANCE_RAISED ||
          status === GRIEVANCE_STATUS.GRIEVANCE_INPROCESS ||
          status === GRIEVANCE_STATUS.GRIEVANCE_L1_ESCALATED ||
          status === GRIEVANCE_STATUS.GRIEVANCE_L2_ESCALATED
        ) {
          inProcessCnt++;
        }
      });

      setNewCount(newCnt);
      setInProgressCount(inProcessCnt);
      setResolvedCount(resolvedCnt);
    } else {
      setNewCount(0);
      setInProgressCount(0);
      setResolvedCount(0);
    }
  }, [grievanceData]);

  const navigate = useNavigate();
  const createGrievanceRequest = () => {
    navigate("/createRequest");
  };

  const customerIdentity = useSelector((state) => state.common.customer_id);

  const [emailMobile, setEmailMobile] = useState("");
  const [grievanceSearch, setGrievanceSearch] = useState("");

  const [disableRaiseButton, setDisableRaiseButton] = useState(false);
  const [dynamicTranslations, setDynamicTranslations] = useState({}); // Store translated dynamic content

  // Translation inputs for static text in Requests page
  const translationInputs = useMemo(
    () => [
      { id: "requests_title", source: "Requests" },
      {
        id: "requests_description",
        source:
          "View and track your grievance requests and respond when required.",
      },
      {
        id: "requests_search_placeholder",
        source: "Search grievance ID, status, or type",
      },
      { id: "requests_badge", source: "Grievance redressal" },
      { id: "requests_raise_new", source: "Raise new request" },
      { id: "requests_new", source: "New" },
      { id: "requests_in_process", source: "In Process" },
      { id: "requests_resolved", source: "Resolved" },
      { id: "requests_grievance_id", source: "Grievance ID" },
      { id: "requests_date_submitted", source: "Date submitted" },
      { id: "requests_last_updated", source: "Last updated" },
      { id: "requests_grievance_type", source: "Grievance type" },
      { id: "requests_remarks", source: "Remarks" },
      { id: "requests_status", source: "Status" },
      { id: "requests_actions", source: "Actions" },
      { id: "requests_feedback", source: "Feedback" },
      { id: "requests_no_data", source: "No Data to Display" },
      // Dynamic status values
      { id: "requests_status_raised", source: "RAISED" },
      { id: "requests_status_inprocess", source: "INPROCESS" },
      { id: "requests_status_l1_escalated", source: "L1_ESCALATED" },
      { id: "requests_status_l2_escalated", source: "L2_ESCALATED" },
      { id: "requests_status_resolved", source: "RESOLVED" },
      { id: "requests_status_new", source: "NEW" },
      { id: "requests_status_to_do", source: "TO_DO" },
      // Grievance type categories
      { id: "requests_type_consent_related", source: "Consent Related Grievance" },
      { id: "requests_type_data_principal", source: "Data Principal Rights" },
      { id: "requests_type_legal", source: "Legal Grievances" },
    ],
    []
  );

  // Use translation hook
  const { getTranslation, translateContent, currentLanguage, isTranslating } =
    useTranslation(translationInputs);

  // Helper to translate dynamic status values (API returns "state", e.g. TO_DO)
  const getTranslatedStatus = (status) => {
    const statusMap = {
      "RAISED": "requests_status_raised",
      "INPROCESS": "requests_status_inprocess",
      "L1_ESCALATED": "requests_status_l1_escalated",
      "L2_ESCALATED": "requests_status_l2_escalated",
      "RESOLVED": "requests_status_resolved",
      "NEW": "requests_status_new",
      "TO_DO": "requests_status_to_do",
    };
    const translationId = statusMap[status?.toUpperCase()];
    return translationId ? getTranslation(translationId, status) : status;
  };

  // Helper to translate grievance type categories
  const getTranslatedGrievanceType = (type) => {
    const typeMap = {
      "Consent Related Grievance": "requests_type_consent_related",
      "Data Principal Rights": "requests_type_data_principal",
      "Legal Grievances": "requests_type_legal",
    };
    const translationId = typeMap[type];
    return translationId ? getTranslation(translationId, type) : type;
  };

  // Helper to get dynamic translation with fallback
  const getDynamicTranslation = (text) => {
    if (!text || currentLanguage === "ENGLISH") return text;
    return dynamicTranslations[text] || text;
  };

  const filteredGrievanceData = useMemo(() => {
    if (!Array.isArray(grievanceData)) return grievanceData;
    const q = (grievanceSearch || "").trim().toLowerCase();
    if (!q) return grievanceData;
    return grievanceData.filter((g) => {
      const hay = [
        g?.grievanceId,
        g?.createdAt,
        g?.updatedAt,
        g?.grievanceType,
        g?.grievanceDescription,
        g?.state,
        g?.status,
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [grievanceData, grievanceSearch]);

  // Function to translate dynamic table data (remarks/descriptions)
  const translateDynamicData = async (data, targetLang) => {
    if (!data || data.length === 0 || targetLang === "en") {
      setDynamicTranslations({});
      return;
    }

    // Collect all unique translatable text
    const uniqueTexts = new Set();
    data.forEach((item) => {
      if (item.grievanceDescription) uniqueTexts.add(item.grievanceDescription);
    });

    if (uniqueTexts.size === 0) return;

    // Create translation inputs
    const inputs = Array.from(uniqueTexts).map((text, idx) => ({
      id: `dynamic_${idx}`,
      source: text,
    }));

    try {
      const response = await dispatch(translateData("en", targetLang, inputs));
      if (response?.status === 200 && response?.data?.output) {
        const translatedMap = {};
        response.data.output.forEach((item, idx) => {
          const originalText = inputs[idx].source;
          translatedMap[originalText] = item.target;
        });
        setDynamicTranslations(translatedMap);
      }
    } catch (error) {
      console.log("Error translating dynamic data:", error);
    }
  };

  // Translate content when language changes
  useEffect(() => {
    if (currentLanguage !== "ENGLISH") {
      translateContent(translationInputs, currentLanguage);
      // Also translate dynamic table data (remarks/descriptions)
      if (grievanceData && grievanceData.length > 0) {
        const targetLang = getApiValueFromLanguage(currentLanguage);
        translateDynamicData(grievanceData, targetLang);
      }
    } else {
      setDynamicTranslations({}); // Reset for English
    }
  }, [currentLanguage, translationInputs, translateContent, grievanceData]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const mobileRegex = /^[6-9]\d{9}$/;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        let emailMobile = "";

        if (mobileRegex.test(customerIdentity)) {
          emailMobile = "mob";
        } else if (emailRegex.test(customerIdentity)) {
          emailMobile = "ema";
        }

        const searchList = await dispatch(
          getGrievnaceRequestByGrievanceTemplateId()
        );

        if (emailMobile === "mob" && searchList.includes("Mobile Number")) {
          let res = await dispatch(
            getGrievnaceRequestByUserId("Mobile Number")
          );

          setGrievanceData(res?.data?.data);
        }

        if (emailMobile === "ema" && searchList.includes("Email address")) {
          let res = await dispatch(
            getGrievnaceRequestByUserId("Email address")
          );
          setGrievanceData(res?.data?.data);
        }
      } catch (err) {
        setDisableRaiseButton(true);
        if (err.code === "ERR_NETWORK") {
          setSessionExpired(true);
        }
        console.log(
          "Error occured in getConsentsByTemplateId",
          JSON.stringify(err)
        );
        if (err?.[0]?.errorCode === "JCMP3001") {
          setGrievanceData([]);
        }
      } finally {
        setLoadLogs(false);
      }
    };

    fetchData();
  }, [customerIdentity, dispatch]);

  return (
    <main role="main" aria-label="Grievance Requests Page">
      <div className="pageConfig">
        <header
          className="fiduciaryHeaderContainer requests-page-header"
          role="banner"
        >
          <div className="fiduciaryHeader">
            <h1>
              <Text appearance="heading-s" color="primary-grey-100">
                {getTranslation("requests_title", "Requests")}
              </Text>
            </h1>
            <div className="badge" role="status" aria-label="Page type: Grievance redressal">
              <Text appearance="body-xs-bold" color="primary-grey-80">
                {getTranslation("requests_badge", "Grievance redressal")}
              </Text>
            </div>
            {sessionExpired && (
              <SessionExpiredModal
                isOpen={sessionExpired}
                onClose={() => setSessionExpired(false)}
              />
            )}
          </div>
          <div className="requestsButtonContainer">
            <ActionButton
              kind="primary"
              size="large"
              label={getTranslation("requests_raise_new", "Raise new request")}
              onClick={createGrievanceRequest}
              state={disableRaiseButton ? "disabled" : "normal"}
              ariaLabel="Raise a new grievance request"
            ></ActionButton>
          </div>
        </header>
        <div className="requests-page-meta-row">
          <div className="pending-requests-description-wrap">
            <Text appearance="body-s" color="primary-grey-80">
              {getTranslation(
                "requests_description",
                "View and track your grievance requests and respond when required.",
              )}
            </Text>
          </div>
          <div className="fiduciaryHeaderButtonContainer requests-page-toolbar">
            <div
              className="search-input-wrapper page-toolbar-search"
              style={{ width: "100%" }}
            >
              <Icon ic={<IcSearch />} size="m" color="primary_grey_100" />
              <input
                type="text"
                className="search-input"
                placeholder={getTranslation(
                  "requests_search_placeholder",
                  "Search grievance ID, status, or type",
                )}
                value={grievanceSearch}
                onChange={(e) => setGrievanceSearch(e.target.value)}
                aria-label={getTranslation(
                  "requests_search_placeholder",
                  "Search grievance ID, status, or type",
                )}
              />
            </div>
          </div>
        </div>
        <div className="requests-status-cards">
          <div
            style={{
              width: "214px",
              height: "90px",
              backgroundColor: "rgba(245, 245, 245, 1)",
              borderRadius: "12px",
              display: "flex",

              gap: "0.5rem",
              alignItems: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                paddingTop: "1rem",
                paddingLeft: "1rem",
                paddingBottom: "1rem",
                gap: "0.5rem",
                alignItems: "center",
              }}
            >
              <IcInfo height={35} width={35} />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Text appearance="body-xs-bold" color="primary-grey-100">
                  {newCount || "0"}
                </Text>
                <Text appearance="body-xs" color="primary-grey-80">
                  {getTranslation("requests_new", "New")}
                </Text>
              </div>
            </div>
          </div>
          <div
            style={{
              width: "214px",
              height: "90px",
              backgroundColor: "rgba(231, 235, 248, 1)",
              borderRadius: "12px",
              display: "flex",

              gap: "0.5rem",
              alignItems: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                paddingTop: "1rem",
                paddingLeft: "1rem",
                paddingBottom: "1rem",
                gap: "0.5rem",
                alignItems: "center",
              }}
            >
              <Icon
                ic={<IcTime height={35} width={35} />}
                color="primary_60"
                size="large"
              />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Text appearance="body-xs-bold" color="primary-grey-100">
                  {inProgressCount || "0"}
                </Text>
                <Text appearance="body-xs" color="primary-grey-80">
                  {getTranslation("requests_in_process", "In Process")}
                </Text>
              </div>
            </div>
          </div>

          <div
            style={{
              width: "214px",
              height: "90px",
              backgroundColor: "rgba(233, 247, 233, 1)",
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                paddingTop: "1rem",
                paddingLeft: "1rem",
                paddingBottom: "1rem",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <IcSuccessColored height={35} width={35} />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Text appearance="body-xs-bold" color="primary-grey-100">
                  {resolvedCount || "0"}
                </Text>
                <Text appearance="body-xs" color="primary-grey-80">
                  {getTranslation("requests_resolved", "Resolved")}
                </Text>
              </div>
            </div>
          </div>
        </div>

        <div
          className="responsive-table-scroll"
          style={{ overflowX: "auto" }}
          role="region"
          aria-label="Grievance Requests Table"
          tabIndex={0}
        >
          <table
            className="consentLog-table grievance-requests-table"
            role="table"
            aria-label="List of grievance requests with details"
          >
            <thead>
              <tr role="row">
                <th className="parentheader" scope="col" role="columnheader">
                  <div className="header-with-icon">
                    <Text appearance="body-xs-bold" color="primary-grey-80">
                      {getTranslation("requests_grievance_id", "Grievance ID")}
                    </Text>
                    <Icon ic={<IcSort />} color="primary_60" size="small" aria-hidden="true" />
                  </div>
                </th>
                <th className="parentheader" scope="col" role="columnheader">
                  <div className="header-with-icon">
                    <Text appearance="body-xs-bold" color="primary-grey-80">
                      {getTranslation("requests_date_submitted", "Date submitted")}
                    </Text>
                    <Icon ic={<IcSort />} color="primary_60" size="small" aria-hidden="true" />
                  </div>
                </th>
                <th className="parentheader" scope="col" role="columnheader">
                  <div className="header-with-icon">
                    <Text appearance="body-xs-bold" color="primary-grey-80">
                      {getTranslation("requests_last_updated", "Last updated")}
                    </Text>
                    <Icon ic={<IcSort />} color="primary_60" size="small" aria-hidden="true" />
                  </div>
                </th>
                <th className="parentheader" scope="col" role="columnheader">
                  <div className="header-with-icon">
                    <Text appearance="body-xs-bold" color="primary-grey-80">
                      {getTranslation("requests_grievance_type", "Grievance type")}
                    </Text>
                    <Icon ic={<IcSort />} color="primary_60" size="small" aria-hidden="true" />
                  </div>
                </th>
                <th className="parentheader" scope="col" role="columnheader">
                  <div className="header-with-icon">
                    <Text appearance="body-xs-bold" color="primary-grey-80">
                      {getTranslation("requests_remarks", "Remarks")}
                    </Text>
                  </div>
                </th>
                <th className="parentheader" scope="col" role="columnheader">
                  <div className="header-with-icon">
                    <Text appearance="body-xs-bold" color="primary-grey-80">
                      {getTranslation("requests_status", "Status")}
                    </Text>
                    <Icon ic={<IcSort />} color="primary_60" size="small" aria-hidden="true" />
                  </div>
                </th>
                <th className="parentheader" scope="col" role="columnheader">
                  <div className="header-with-icon">
                    <Text appearance="body-xs-bold" color="primary-grey-80">
                      {getTranslation("requests_actions", "Actions")}
                    </Text>
                  </div>
                </th>
                {/* Feedback column — kept for future use
                <th className="parentheader" scope="col" role="columnheader">
                  <div className="header-with-icon">
                    <Text appearance="body-xs-bold" color="primary-grey-80">
                      {getTranslation("requests_feedback", "Feedback")}
                    </Text>
                  </div>
                </th>
                */}
              </tr>
            </thead>

            <tbody>
              {loadLogs ? (
                <tr>
                  <td
                    colSpan="7"
                    style={{ textAlign: "center", padding: "1rem" }}
                  >
                    <div className="customerActivityLoader">
                      <Spinner
                        kind="normal"
                        labelPosition="right"
                        size="small"
                      />
                    </div>
                  </td>
                </tr>
              ) : !Array.isArray(grievanceData) ||
                grievanceData.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    style={{ textAlign: "center", padding: "1rem" }}
                  >
                    <Text appearance="body-xs-bold" color="primary-grey-80">
                      {getTranslation("requests_no_data", "No Data to Display")}
                    </Text>
                  </td>
                </tr>
              ) : !Array.isArray(filteredGrievanceData) ||
                filteredGrievanceData.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    style={{ textAlign: "center", padding: "1rem" }}
                  >
                    <Text appearance="body-xs-bold" color="primary-grey-80">
                      {getTranslation("requests_no_data", "No Data to Display")}
                    </Text>
                  </td>
                </tr>
              ) : (
                Array.isArray(filteredGrievanceData) &&
                filteredGrievanceData?.map((grievance, index) => (
                  <tr
                    key={grievance?.grievanceId || `grievance-${index}`}
                    className={
                      expandedRow === `row${index}` ? "expanded-row-parent" : ""
                    }
                  >
                    <td className="grievance-id-cell" title={grievance.grievanceId || ""}>
                      <span className="grievance-id-text">
                        {grievance.grievanceId || ""}
                      </span>
                    </td>
                    <td className="grievance-date-cell">
                      <Text appearance="body-xs" color="primary-grey-100">
                        {formatGrievanceDate(grievance.createdAt)}
                      </Text>
                    </td>
                    <td className="grievance-date-cell">
                      <Text appearance="body-xs" color="primary-grey-100">
                        {formatGrievanceDate(grievance.updatedAt)}
                      </Text>
                    </td>
                    <td className="grievance-type-cell">
                      <Text appearance="body-xs" color="primary-grey-100">
                        {getTranslatedGrievanceType(grievance.grievanceType) || ""}
                      </Text>
                    </td>
                    <td
                      className="grievance-remarks-cell"
                      title={
                        getDynamicTranslation(grievance.grievanceDescription) ||
                        ""
                      }
                    >
                      <span className="grievance-remarks-text">
                        {getDynamicTranslation(grievance.grievanceDescription) || ""}
                      </span>
                    </td>
                    <td className="grievance-status-cell">
                      <span
                        className={getGrievanceStatusBadgeClass(
                          grievance?.state || grievance?.status
                        )}
                      >
                        {getTranslatedStatus(
                          grievance.state || grievance.status
                        ) || ""}
                      </span>
                    </td>

                    <td className="grievance-actions-cell">
                      <button
                        type="button"
                        className="grievance-view-action"
                        aria-label={`View grievance ${grievance.grievanceId}`}
                        onClick={() =>
                          grievance?.grievanceId &&
                          navigate(`/requests/${grievance.grievanceId}`)
                        }
                      >
                        <IcVisible height={20} width={20} aria-hidden="true" />
                      </button>
                    </td>
                    {/* Feedback column — kept for future use
                    <td className="grievance-feedback-cell">
                      {grievance.status ===
                        GRIEVANCE_STATUS.GRIEVANCE_RESOLVED && (
                        <StarRating
                          className="grievance-feedback-stars"
                          iconSize="s"
                          initialRating={grievance.feedback || 0}
                          readOnly={false}
                          grievanceId={grievance.grievanceId}
                        />
                      )}
                    </td>
                    */}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
};
export default Requests;
